import isNil from 'lodash/isNil';

import { isNilOrEmpty } from '../objectUtils';
import {
  BankAccount,
  BankAccountType,
  CounterpartyIdentityKeyType,
  CounterpartyIdentityKind,
} from '../types/model-app';

import { buildCounterpartyIdentityId, normalizeIban } from './counterpartyIdentity';
import { LearnedIdentity } from './counterpartyIdentityLearning';

const IDENTITY_KIND_BY_BANK_ACCOUNT_TYPE: Record<BankAccountType, CounterpartyIdentityKind> = {
  [BankAccountType.AGENCY_ACCOUNT]: CounterpartyIdentityKind.AGENCY_ACCOUNT,
  [BankAccountType.OWNER_PRIVATE_ACCOUNT]: CounterpartyIdentityKind.OWNER_ACCOUNT,
  [BankAccountType.SUPPLIER_ACCOUNT]: CounterpartyIdentityKind.SUPPLIER,
  [BankAccountType.THIRD_PARTY_BUILDING]: CounterpartyIdentityKind.INTERNAL_ACCOUNT,
  [BankAccountType.THIRD_PARTY_MULTI_OWNER]: CounterpartyIdentityKind.INTERNAL_ACCOUNT,
  [BankAccountType.THIRD_PARTY_OWNER]: CounterpartyIdentityKind.INTERNAL_ACCOUNT,
};

/**
 * An IBAN entered on a contact or connected as one of the client's accounts is an identity before any payment:
 * a supplier or an owner is recognised from its first movement, a client account marks a transfer (P2.2, P4.1).
 */
export const getIdentityOfBankAccount = (
  bankAccount: Pick<BankAccount, 'number' | 'type' | 'contactId' | 'clientId' | 'disabled' | 'holderName' | '_deleted'>
): LearnedIdentity | null => {
  if (bankAccount._deleted || bankAccount.disabled || isNilOrEmpty(bankAccount.number)) {
    return null;
  }
  const kind = IDENTITY_KIND_BY_BANK_ACCOUNT_TYPE[bankAccount.type];
  const key = normalizeIban(bankAccount.number);
  if (isNil(kind) || isNilOrEmpty(key)) {
    return null;
  }
  return {
    id: buildCounterpartyIdentityId(bankAccount.clientId, CounterpartyIdentityKeyType.IBAN, key),
    keyType: CounterpartyIdentityKeyType.IBAN,
    key,
    kind,
    contactId: isNilOrEmpty(bankAccount.contactId) ? null : bankAccount.contactId,
    exclusionReason: null,
    exclusionComment: null,
    lastCounterpartName: bankAccount.holderName ?? null,
  };
};
